/*
 * Parts List (favourites / saved products)
 * --------------------------------------------------------------
 * Theme-native wishlist stored in the browser via localStorage so it
 * works for both guests and logged-in customers without a 3rd-party app.
 *
 * Public surface:
 *   window.PartsList                       -> store API (get/add/remove/toggle/has/count)
 *   <parts-list-toggle data-product="..."> -> add/remove button (PDP + cards)
 *   <parts-list-count>                     -> header badge, auto-updates
 *   <parts-list-grid>                      -> renders saved items on the Parts List page
 *   document event 'parts-list:change'     -> {count, items}
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'rs_parts_list_v1';

  var PartsList = {
    /** @returns {Array} saved items */
    get: function () {
      try {
        var raw = window.localStorage.getItem(STORAGE_KEY);
        var items = raw ? JSON.parse(raw) : [];
        return Array.isArray(items) ? items : [];
      } catch (e) {
        return [];
      }
    },

    save: function (items) {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      } catch (e) {
        /* storage full / disabled — fail quietly */
      }
      this._emit();
    },

    has: function (id) {
      id = String(id);
      return this.get().some(function (i) {
        return String(i.id) === id;
      });
    },

    add: function (item) {
      if (!item || item.id == null) return;
      var items = this.get();
      if (!this.has(item.id)) {
        items.push(item);
        this.save(items);
      }
    },

    remove: function (id) {
      id = String(id);
      this.save(
        this.get().filter(function (i) {
          return String(i.id) !== id;
        })
      );
    },

    /** Add if missing, remove if present. @returns {boolean} isInList after toggle */
    toggle: function (item) {
      if (this.has(item.id)) {
        this.remove(item.id);
        return false;
      }
      this.add(item);
      return true;
    },

    count: function () {
      return this.get().length;
    },

    clear: function () {
      this.save([]);
    },

    _emit: function () {
      document.dispatchEvent(
        new CustomEvent('parts-list:change', {
          detail: { count: this.count(), items: this.get() }
        })
      );
    }
  };

  window.PartsList = PartsList;

  /* ----------------------------------------------------------------
   * <parts-list-toggle> — the heart / "Add to Parts list" button.
   * Expects child <button> and data attributes describing the product.
   * ---------------------------------------------------------------- */
  if (!customElements.get('parts-list-toggle')) {
    customElements.define(
      'parts-list-toggle',
      class extends HTMLElement {
        connectedCallback() {
          this.button = this.querySelector('button') || this;
          this.id_ = this.dataset.id;
          this._sync();
          this.button.addEventListener('click', this._onClick.bind(this));
          this._onChange = this._sync.bind(this);
          document.addEventListener('parts-list:change', this._onChange);
        }

        disconnectedCallback() {
          document.removeEventListener('parts-list:change', this._onChange);
        }

        _item() {
          var d = this.dataset;
          return {
            id: d.id,
            handle: d.handle || '',
            title: d.title || '',
            url: d.url || '',
            image: d.image || '',
            price: d.price || '',
            vendor: d.vendor || '',
            available: d.available !== 'false'
          };
        }

        _onClick(e) {
          e.preventDefault();
          var added = PartsList.toggle(this._item());
          this._sync();
          if (added) {
            // Tell guests they can save the list to their account.
            if (this.dataset.loggedIn === 'false') {
              window.PartsListModal && window.PartsListModal.open(this.dataset.title);
            }
          }
        }

        _sync() {
          var active = PartsList.has(this.id_);
          this.classList.toggle('is-active', active);
          this.button.setAttribute('aria-pressed', active ? 'true' : 'false');
          var added = this.button.querySelector('[data-pl-added]');
          var add = this.button.querySelector('[data-pl-add]');
          if (added) added.hidden = !active;
          if (add) add.hidden = active;
          var label = active
            ? this.dataset.labelAdded || 'In Parts list'
            : this.dataset.labelAdd || 'Add to Parts list';
          this.button.setAttribute('aria-label', label);
          var text = this.button.querySelector('[data-pl-text]');
          if (text) text.textContent = label;
        }
      }
    );
  }

  /* ----------------------------------------------------------------
   * <parts-list-count> — header badge. Hides itself when empty.
   * ---------------------------------------------------------------- */
  if (!customElements.get('parts-list-count')) {
    customElements.define(
      'parts-list-count',
      class extends HTMLElement {
        connectedCallback() {
          this._render = this._render.bind(this);
          this._render();
          document.addEventListener('parts-list:change', this._render);
        }
        disconnectedCallback() {
          document.removeEventListener('parts-list:change', this._render);
        }
        _render() {
          var n = PartsList.count();
          this.textContent = n > 99 ? '99+' : String(n);
          this.hidden = n === 0;
        }
      }
    );
  }

  /* ----------------------------------------------------------------
   * <parts-list-grid> — renders saved products on the Parts List page.
   * Reads a JSON config from data-money-format / data-empty-url etc.
   * ---------------------------------------------------------------- */
  if (!customElements.get('parts-list-grid')) {
    customElements.define(
      'parts-list-grid',
      class extends HTMLElement {
        connectedCallback() {
          this._render = this._render.bind(this);
          this._render();
          document.addEventListener('parts-list:change', this._render);
          this.addEventListener('click', this._onClick.bind(this));
        }
        disconnectedCallback() {
          document.removeEventListener('parts-list:change', this._render);
        }
        _onClick(e) {
          var rm = e.target.closest('[data-pl-remove]');
          if (rm) {
            e.preventDefault();
            PartsList.remove(rm.dataset.plRemove);
          }
          var clear = e.target.closest('[data-pl-clear]');
          if (clear) {
            e.preventDefault();
            if (window.confirm('Remove all items from your Parts list?')) PartsList.clear();
          }
        }
        _render() {
          var items = PartsList.get();
          var empty = this.querySelector('[data-pl-empty]');
          var list = this.querySelector('[data-pl-items]');
          var toolbar = this.querySelector('[data-pl-toolbar]');
          if (!list) return;
          if (toolbar) toolbar.hidden = items.length === 0;
          if (empty) empty.hidden = items.length > 0;
          if (items.length === 0) {
            list.innerHTML = '';
            return;
          }
          list.innerHTML = items
            .map(function (i) {
              var img = i.image
                ? '<img src="' + i.image + '" alt="" loading="lazy" width="160" height="160">'
                : '<span class="parts-list-card__noimg"></span>';
              var price = i.price ? '<p class="parts-list-card__price">' + i.price + '</p>' : '';
              var vendor = i.vendor
                ? '<p class="parts-list-card__vendor">' + escapeHtml(i.vendor) + '</p>'
                : '';
              return (
                '<li class="parts-list-card">' +
                '<a class="parts-list-card__media" href="' + i.url + '">' + img + '</a>' +
                '<div class="parts-list-card__body">' +
                vendor +
                '<a class="parts-list-card__title" href="' + i.url + '">' + escapeHtml(i.title) + '</a>' +
                price +
                '<div class="parts-list-card__actions">' +
                '<a class="button button--small" href="' + i.url + '">View product</a>' +
                '<button type="button" class="parts-list-card__remove link" data-pl-remove="' + i.id + '">Remove</button>' +
                '</div></div></li>'
              );
            })
            .join('');
        }
      }
    );
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ----------------------------------------------------------------
   * Login prompt modal (shown to guests after their first save).
   * ---------------------------------------------------------------- */
  window.PartsListModal = {
    open: function (productTitle) {
      var modal = document.getElementById('PartsListLoginModal');
      if (!modal) return;
      var slot = modal.querySelector('[data-pl-modal-product]');
      if (slot) slot.textContent = productTitle ? '“' + productTitle + '” was added.' : '';
      modal.removeAttribute('hidden');
      document.body.classList.add('parts-list-modal-open');
      var focusable = modal.querySelector('a, button');
      if (focusable) focusable.focus();
    },
    close: function () {
      var modal = document.getElementById('PartsListLoginModal');
      if (!modal) return;
      modal.setAttribute('hidden', '');
      document.body.classList.remove('parts-list-modal-open');
    }
  };

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-pl-modal-close]')) {
      e.preventDefault();
      window.PartsListModal.close();
    }
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') window.PartsListModal.close();
  });

  // Fire once on load so badges/buttons reflect stored state.
  document.addEventListener('DOMContentLoaded', function () {
    PartsList._emit();
  });
})();
